/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RightArrow } from '../assets/svg/ArrowIcon';
import { Activity, formatAmount, webFormatCurrency } from '@rb-serve-app/core';
import moment from 'moment';
import { EventType, useAnalytics } from '../ui/Analytics';
import ContactUs from '../screens/ContactUs';

// Extend Activity type to include EPP properties
interface ExtendedActivity extends Activity {
  eligibleforEPP?: boolean;
  eppType?: string;
  name?: {
    nameOnCard?: string;
  };
}

// Define props types
interface TransactionDetailsProps {
  onClose: () => void;
  setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  transactionDetails?: ExtendedActivity | undefined;
  featureFlaggingData: any;
  setShowFeatureFlagMessage: React.Dispatch<React.SetStateAction<boolean>>;
  rb_epp?: any;
  accountId?: string;
  customerId?: string;
}

const TransactionDetails: React.FC<TransactionDetailsProps> = ({
  onClose,
  setModalIsOpen,
  transactionDetails,
  featureFlaggingData,
  setShowFeatureFlagMessage,
  rb_epp,
  accountId,
  customerId,
}) => {
  const { contentfulData } = useSelector(
    (state: { contentful: any }) => state.contentful
  );
  const [isContactUsOpened, setIsContactUsOpened] = useState(false);
  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();
  const lang = localStorage.getItem('language') || 'en-US';

  const setupLocale = () => {
    try {
      const localeToUse = lang?.includes('fr') ? 'fr' : 'en';
      moment.locale(localeToUse);
      return localeToUse;
    } catch (error) {
      console.warn('Error setting moment locale:', error);
      moment.locale('en');
      return 'en';
    }
  };

  setupLocale();
  const formatDate = (dateString: string) => moment(dateString).format('ll');

  const handleApplyForEPP = () => {
    // Close the modal
    setModalIsOpen(false);

    // Navigate to SelectEqualPaymentPlan screen with transaction details
    navigate('/select-equal-payment-plan', {
      state: {
        transaction: transactionDetails,
        accountId: accountId,
        customerId: customerId,
      },
    });

    // Track the event
    trackEvent(
      EventType.CLICK_EVENT,
      'modal transaction details',
      window.location.href,
      'modal transaction details|apply for 0% equal payment plan'
    );
  };

  // const formatActivityCategory = (category: any) => {
  //   if (!category) return '';
  //   return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
  // };

  const { entries, assets } = contentfulData;
  const { rb_transaction_history } = entries;

  return (
    <div className="h-full w-full rounded-lg bg-white overflow-auto max-h-[100vh] mb-7">
      <div className="flex flex-row bg-white px-4 py-6 rounded-md w-full">
        <h2
          aria-label={rb_transaction_history.transactionDetailsTitle}
          className="flex-1 font-[650] text-[22px]"
        >
          {rb_transaction_history.transactionDetailsTitle}
        </h2>
        <div onClick={onClose} className="close-btn cursor-pointer pr-2">
          <img
            src={assets.popup_close_icon}
            className="w-6 h-6 p-0 m-0"
            alt="PopupCloseIcon"
          />
        </div>
      </div>
      <div className="w-full h-[1px] bg-[#e9e9e9]"></div>
      <div className="bg-[#fafafa] p-5 ml-5 mr-5 mt-5 items-center flex flex-row">
        <p
          aria-label={transactionDetails?.merchant.name}
          className="flex flex-1 text-xl"
        >{`${transactionDetails?.merchant.name}`}</p>
        <span className="flex flex-row items-center">
          {transactionDetails?.foreign ? (
            <div className="flex flex-row relative pl-10">
              <span className="material-icons absolute left-3 bottom-[1px]">
                <img
                  src={assets?.fx_logo}
                  alt="foreign transactionDetails"
                  className="w-5 h-5"
                />
              </span>
              <span aria-label={transactionDetails?.amount.value}>
                {Number(transactionDetails?.amount.value) < 0
                  ? `-${webFormatCurrency(Math.abs(Number(transactionDetails?.amount.value)), lang)}`
                  : `${webFormatCurrency(Number(transactionDetails?.amount.value), lang)}`}
              </span>
            </div>
          ) : (
            <div className="flex flex-row pl-10">
              <span className="" aria-label={transactionDetails?.amount.value}>
                {Number(transactionDetails?.amount.value) < 0
                  ? `-${webFormatCurrency(Math.abs(Number(transactionDetails?.amount.value)), lang)}`
                  : `${webFormatCurrency(Number(transactionDetails?.amount.value), lang)}`}
              </span>
            </div>
          )}
        </span>
      </div>

      {/* EPP Section for eligible transactions */}
      {transactionDetails?.eligibleforEPP && (
        <div className="mx-5 mb-5 mt-5">
          <div
            className="flex items-center gap-[10px] px-[15px] py-[8px] bg-[#F5F5F5]"
            style={{
              borderRadius: '10px 10px 0px 0px',
              borderTop: '1px solid #E9E9E9',
              borderRight: '1px solid #E9E9E9',
              borderLeft: '1px solid #E9E9E9',
            }}
          >
            <p
              className="text-[#1F1F1F] text-[16px] font-normal leading-[20px] tracking-[-0.051px]"
              style={{ fontFamily: 'Ted Next' }}
            >
              {transactionDetails?.eppType === '0%'
                ? rb_epp?.eppWebConfig?.transactionTableEpp
                    ?.transactionDetails0Epp?.title ||
                  'What is a 0% equal payment plan?'
                : rb_epp?.eppWebConfig?.transactionTableEpp
                    ?.transactionDetailsNon0Epp?.title ||
                  'What is an equal payment plan?'}
            </p>
          </div>
          <div className="border border-[#E9E9E9] bg-white rounded-b-[10px] px-[15px] py-[12px]">
            <p
              className="text-[#1F1F1F] text-[16px] font-normal leading-[20px] tracking-[-0.051px] mb-4"
              style={{
                fontFamily: 'Ted Next',
                alignSelf: 'stretch',
              }}
            >
              {transactionDetails?.eppType === '0%'
                ? rb_epp?.eppWebConfig?.transactionTableEpp
                    ?.transactionDetails0Epp?.description ||
                  'An Equal Payment Plan allows you to convert large purchases into equal monthly payments so you can pay at a pace that works for you.'
                : rb_epp?.eppWebConfig?.transactionTableEpp
                    ?.transactionDetailsNon0Epp?.description ||
                  'An Equal Payment Plan allows you to convert large purchases into equal monthly payments so you can pay at a pace that works for you.'}
            </p>
            <button
              onClick={handleApplyForEPP}
              className="bg-[#DA291C] text-white px-4 py-2 rounded font-medium hover:bg-red-700 transition-colors"
            >
              {transactionDetails?.eppType === '0%'
                ? rb_epp?.eppWebConfig?.transactionTableEpp
                    ?.transactionDetails0Epp?.cta ||
                  'Apply for 0% Equal Payment Plan'
                : rb_epp?.eppWebConfig?.transactionTableEpp
                    ?.transactionDetailsNon0Epp?.cta ||
                  'Apply for Equal Payment Plan'}
            </button>
          </div>
        </div>
      )}

      <div className="p-2 md:mb-0">
        <div className="flex flex-wrap justify-between">
          <div className="flex-1 min-w-[200px] p-4">
            <div className="mb-4">
              <p
                className="text-[#1f1f1f] font-medium"
                aria-label={rb_transaction_history.statusTitle}
              >
                {rb_transaction_history.statusTitle}
              </p>
              <p
                aria-label={transactionDetails?.activityStatus}
                className={`font-[650] ${transactionDetails?.activityStatus === 'APPROVED' ? 'text-[#1E8240]' : 'text-[#666666]'}`}
              >
                {transactionDetails?.activityStatus}
              </p>
            </div>
            <div className="mb-4">
              <p
                className="text-[#1f1f1f] font-medium"
                aria-label={rb_transaction_history.merchantProvinceStateTitle}
              >
                {rb_transaction_history.merchantProvinceStateTitle}
              </p>
              <p
                className="font-[650] text-black"
                aria-label={
                  transactionDetails?.merchant?.address?.stateProvince
                }
              >
                {transactionDetails?.merchant?.address?.stateProvince || '--'}
              </p>
            </div>
            <div className="mb-4">
              <p
                className="text-[#1f1f1f] font-medium"
                aria-label={rb_transaction_history.merchantPostalcodeZipTitle}
              >
                {rb_transaction_history.merchantPostalcodeZipTitle}
              </p>
              <p
                className="font-[650] text-black"
                aria-label={transactionDetails?.merchant?.address?.postalCode}
              >
                {transactionDetails?.merchant?.address?.postalCode || '--'}
              </p>
            </div>
            <div className="mb-4">
              <p
                className="text-[#1f1f1f] font-medium"
                aria-label={rb_transaction_history.purchaseAmountTitle}
              >
                {rb_transaction_history.purchaseAmountTitle}
              </p>
              <p
                className="font-[650] text-black"
                aria-label={transactionDetails?.foreign?.originalAmount?.value}
              >
                {transactionDetails?.foreign?.originalAmount?.value
                  ? formatAmount(
                      transactionDetails?.foreign?.originalAmount?.value
                    )
                  : formatAmount(
                      transactionDetails?.foreign?.originalAmount?.value
                    ) || '--'}
              </p>
            </div>
            {Number(transactionDetails?.foreign?.conversionMarkupRate) > 0 && (
              <div className="mb-4">
                <p
                  className="text-[#1f1f1f] font-medium"
                  aria-label={rb_transaction_history.conversionRateTitle}
                >
                  {rb_transaction_history.conversionRateTitle}
                </p>
                <p
                  className="font-[650] text-black"
                  aria-label={transactionDetails?.foreign?.conversionMarkupRate}
                >
                  {transactionDetails?.foreign?.conversionMarkupRate}
                </p>
              </div>
            )}
            <div className="mb-4">
              <p
                className="text-[#1f1f1f] font-medium"
                aria-label={rb_transaction_history.merchantCategoryTitle}
              >
                {rb_transaction_history.merchantCategoryTitle}
              </p>
              <p
                className="font-[650] text-black"
                aria-label={transactionDetails?.merchant.category}
              >
                {transactionDetails?.merchant.category}
              </p>
            </div>
            <div className="mb-4">
              <p
                className="text-[#1f1f1f] font-medium"
                aria-label={rb_transaction_history.transactionCardNumberTitle}
              >
                {rb_transaction_history.transactionCardNumberTitle}
              </p>
              <p
                className="font-[650] text-black"
                aria-label={transactionDetails?.cardNumber.slice(-8)}
              >
                {transactionDetails?.cardNumber.slice(-8)}
              </p>
            </div>
          </div>
          <div className="flex-1 min-w-[200px] p-4">
            <div className="mb-4">
              <p
                className="text-[#1f1f1f] font-medium"
                aria-label={rb_transaction_history.transactionDateTitle}
              >
                {rb_transaction_history.transactionDateTitle}
              </p>
              <p
                className="font-[650] text-black"
                aria-label={transactionDetails?.date}
              >
                {formatDate(transactionDetails?.date || '')}
              </p>
            </div>
            <div className="mb-4">
              <p
                className="text-[#1f1f1f] font-medium"
                aria-label={rb_transaction_history.merchantCountryTitle}
              >
                {rb_transaction_history.merchantCountryTitle}
              </p>
              <p
                className="font-[650] text-black"
                aria-label={transactionDetails?.merchant?.address?.countryCode}
              >
                {transactionDetails?.merchant?.address?.countryCode || '--'}
              </p>
            </div>
            <div className="mb-4">
              <p
                className="text-[#1f1f1f] font-medium"
                aria-label={rb_transaction_history.currencyTitle}
              >
                {rb_transaction_history.currencyTitle}
              </p>
              <p
                className="font-[650] text-black"
                aria-label={
                  transactionDetails?.foreign?.originalAmount?.currency
                }
              >
                {transactionDetails?.foreign?.originalAmount?.currency || '--'}
              </p>
            </div>
            <div className="mb-4">
              <p
                className="text-[#1f1f1f] font-medium"
                aria-label={rb_transaction_history.exchangeFeesTitle}
              >
                {rb_transaction_history.exchangeFeesTitle}
              </p>
              <span className="flex flex-row items-center">
                {transactionDetails?.foreign ? (
                  <>
                    <img
                      src={assets?.fx_logo}
                      alt="foreign transaction"
                      className="w-4 h-4 mr-2"
                    />
                    <p
                      className="font-[650] text-black"
                      aria-label={
                        transactionDetails?.foreign?.exchangeFee?.value
                      }
                    >
                      {transactionDetails?.foreign?.exchangeFee?.value || 0}
                    </p>
                  </>
                ) : (
                  '--'
                )}
              </span>
            </div>
            <div className="mb-4">
              <p
                className="text-[#1f1f1f] font-medium"
                aria-label={rb_transaction_history.transactionTypeTitle}
              >
                {rb_transaction_history.transactionTypeTitle}
              </p>
              <p
                className="font-[650] text-black"
                aria-label={transactionDetails?.activityType}
              >
                {transactionDetails?.activityType}
              </p>
            </div>
            <div className="mb-4">
              <p
                className="text-[#1f1f1f] font-medium"
                aria-label={rb_transaction_history.referenceNumberTitle}
              >
                {rb_transaction_history.referenceNumberTitle}
              </p>
              <p
                className="font-[650] text-black"
                aria-label={transactionDetails?.referenceNumber}
              >
                {transactionDetails?.referenceNumber || '--'}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-[#fafafa] flex flex-row p-[30px] items-center rounded-b-lg left-0 right-0">
        <p className="flex flex-1">{rb_transaction_history.needHelpTitle}</p>
        <button
          className={`${featureFlaggingData ? 'bg-[#DA291C] text-white' : 'text-gray-400 bg-gray-100 border-gray-300 border-2'} pl-5 pr-5 pt-3 pb-3 flex flex-row justify-between`}
          onClick={() => {
            setIsContactUsOpened(true);
            // setModalIsOpen(false);
            trackEvent(
              EventType.CLICK_EVENT,
              'modal transaction details',
              window.location.href,
              'modal transaction details|contact us'
            );
            // if (window.scrollY !== 0) {
            //   window.scrollTo({
            //     top: 0,
            //     behavior: 'smooth',
            //   });
            // }
            // setShowFeatureFlagMessage(true);
            // setTimeout(() => {
            //   setShowFeatureFlagMessage(false);
            // }, 3000);
          }}
          aria-label={rb_transaction_history.contactUsCtaTitle}
        >
          {rb_transaction_history.contactUsCtaTitle}
          <RightArrow fill={featureFlaggingData ? '#fff' : '#aaa'} />
        </button>
        {isContactUsOpened && (
          <ContactUs
            isContactUsOpened={isContactUsOpened}
            setIsContactUsOpened={setIsContactUsOpened}
          />
        )}
      </div>
    </div>
  );
};

export default TransactionDetails;
